/**
 * Advisor Token Management
 *
 * Handles localStorage storage of pending advisor invitation tokens
 * with expiration and cleanup to prevent stale token issues.
 *
 * Tokens are stored when an advisor signs up via invitation link
 * and consumed after email verification to complete the connection.
 */

const TOKEN_KEY = 'pending_advisor_token';
const TOKEN_EXPIRY_KEY = 'pending_advisor_token_expiry';
const TOKEN_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Store an advisor invitation token with expiration
 */
export function storeAdvisorToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + TOKEN_TTL_MS));
  } catch (error) {
    // localStorage may be unavailable (private browsing, etc.)
    console.warn('[advisorToken] Failed to store token:', error);
  }
}

/**
 * Retrieve the advisor token if it exists and hasn't expired
 * Returns null if no token, expired, or localStorage unavailable
 */
export function getAdvisorToken(): string | null {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token) {
      return null;
    }

    // Check expiration
    if (expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (!isNaN(expiryTime) && Date.now() > expiryTime) {
        // Token has expired, clean it up
        clearAdvisorToken();
        console.log('[advisorToken] Token expired, cleared');
        return null;
      }
    }

    return token;
  } catch (error) {
    // localStorage may be unavailable
    console.warn('[advisorToken] Failed to get token:', error);
    return null;
  }
}

/**
 * Clear the advisor token and expiry from storage
 * Call this after successfully using the token
 */
export function clearAdvisorToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  } catch (error) {
    console.warn('[advisorToken] Failed to clear token:', error);
  }
}

/**
 * Check if there's a valid (non-expired) advisor token
 */
export function hasAdvisorToken(): boolean {
  return getAdvisorToken() !== null;
}
